import { clientInstance } from '../utils/http-client'
import { PaginatedResponse } from '../models/paginated-response'
import { baseAPICall } from './baseAPICall'
import {
  DealSearchResult,
  EmailContact,
  EmailIntegration,
  EmailMessage,
  EmailSignature,
  EmailThread,
} from 'models/emailIntegration'
import { useQuery } from '@tanstack/react-query'

export async function getEmailIntegrationList(
  params = {},
  signal?: AbortSignal
) {
  return baseAPICall<PaginatedResponse<EmailIntegration>>(() =>
    clientInstance.get(`/email_integration/`, {
      params: {
        ...params,
      },
      signal,
    })
  )
}
async function getEmailIntegrationOptions(withNoneOption?: boolean) {
  return baseAPICall<
    {
      label: string
      value: number
      original?: EmailIntegration
    }[]
  >(() =>
    clientInstance.get(`/email_integration/`, {
      transformResponse: (data) => {
        const response: PaginatedResponse<EmailIntegration> = JSON.parse(data)
        const opts = response.results.map((emailIntegration) => ({
          label: emailIntegration.email,
          value: emailIntegration.id,
          original: emailIntegration as EmailIntegration | undefined,
        }))

        if (withNoneOption) {
          opts.unshift({ label: 'No Email', value: -1, original: undefined })
        }

        return opts
      },
    })
  )
}

export const useGetEmailIntegrationOptions = (withNoneOption?: boolean) =>
  useQuery({
    queryKey: ['email-integrations', withNoneOption ? 'with-none' : ''],
    queryFn: () => getEmailIntegrationOptions(withNoneOption),
  })

export async function getEmailIntegrationAuthUrl({
  provider,
  isShared,
  isCalendarShared,
  email,
  features,
}: {
  provider?: string
  isShared: boolean
  isCalendarShared?: boolean
  email?: string
  features?: string[]
}): Promise<{ auth_url: string }> {
  return baseAPICall(() =>
    clientInstance.get(`/nylas/connect/`, {
      params: {
        is_shared: isShared,
        is_calendar_shared: isCalendarShared ?? false,
        provider,
        ...(email && { email }),
        ...(features?.length && { features: features.join(',') }),
      },
    })
  )
}

export async function reauthenticateEmailIntegration({
  provider,
  isShared,
  email,
}: {
  provider: string
  isShared: boolean
  email?: string
}): Promise<{ auth_url: string }> {
  return getEmailIntegrationAuthUrl({ provider, isShared, email })
}

export async function updateEmailIntegration(
  emailIntegrationId: number,
  data: Partial<{ is_shared: boolean }>
) {
  return baseAPICall(() =>
    clientInstance.patch(`/email_integration/${emailIntegrationId}/`, data)
  )
}

export async function syncEmailIntegration(emailIntegrationId: number) {
  return baseAPICall(() =>
    clientInstance.post(`/email_integration/${emailIntegrationId}/sync/`)
  )
}

/** List the current user's signatures. */
export async function getEmailSignatureList(signal?: AbortSignal) {
  return baseAPICall<PaginatedResponse<EmailSignature>>(() =>
    clientInstance.get(`/email_integration/email_signature/`, {
      params: { limit: 100 },
      signal,
    })
  )
}

/** Create a new signature for the current user. */
export async function createEmailSignature(data: {
  name: string
  html: string
  is_default?: boolean
}) {
  return baseAPICall<EmailSignature>(() =>
    clientInstance.post(`/email_integration/email_signature/`, data)
  )
}

/** Update an existing signature (name, html, is_default). */
export async function updateEmailSignature(
  signatureId: number,
  data: Partial<{ name: string; html: string; is_default: boolean }>
) {
  return baseAPICall<EmailSignature>(() =>
    clientInstance.patch(
      `/email_integration/email_signature/${signatureId}/`,
      data
    )
  )
}

/** Delete a signature. If it was the default, another is auto-promoted. */
export async function deleteEmailSignature(signatureId: number) {
  return baseAPICall(() =>
    clientInstance.delete(`/email_integration/email_signature/${signatureId}/`)
  )
}

/** Promote a signature to be the integration's default. */
export async function setDefaultEmailSignature(signatureId: number) {
  return baseAPICall<EmailSignature>(() =>
    clientInstance.post(
      `/email_integration/email_signature/${signatureId}/set_default/`
    )
  )
}

export async function deleteEmailIntegration(emailIntegrationId: number) {
  return baseAPICall(() =>
    clientInstance.delete(`/email_integration/${emailIntegrationId}/`)
  )
}

export async function getEmailMessageList(params = {}, signal?: AbortSignal) {
  return baseAPICall<PaginatedResponse<EmailMessage>>(() =>
    clientInstance.get(`/email_integration/email_message/`, {
      params: {
        ...params,
      },
      signal,
    })
  )
}

export async function sendEmailMessage(
  emailIntegrationId: number,
  data: FormData
) {
  return baseAPICall(
    () =>
      clientInstance.post(
        `/email_integration/${emailIntegrationId}/send/`,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      ),
    {
      disableNotifyError: true,
      disableNotifyGenericError: true,
    }
  )
}

export async function getEmailMessageById(
  emailId: string | number,
  signal?: AbortSignal
) {
  return baseAPICall<EmailMessage>(() =>
    clientInstance.get(`/email_integration/email_message/${emailId}/`, {
      signal,
    })
  )
}

/** Fetch a paginated list of email threads with filtering/sorting support. */
export async function getEmailThreadList(params = {}, signal?: AbortSignal) {
  return baseAPICall<PaginatedResponse<EmailThread>>(() =>
    clientInstance.get(`/email_integration/email_thread/`, {
      params: {
        ...params,
      },
      signal,
    })
  )
}

/** Fetch all messages in a thread, ordered by date ascending. */
export async function getThreadMessages(
  threadId: number,
  signal?: AbortSignal
) {
  return baseAPICall<EmailMessage[]>(() =>
    clientInstance.get(
      `/email_integration/email_thread/${threadId}/messages/`,
      { signal }
    )
  )
}

/** Fetch distinct sender contacts for the filter dropdown typeahead. */
export async function getDistinctSenders(
  params: { search?: string } = {},
  signal?: AbortSignal
) {
  return baseAPICall<EmailContact[]>(() =>
    clientInstance.get(`/email_integration/email_thread/distinct_senders/`, {
      params,
      signal,
    })
  )
}

/** Fetch distinct recipient contacts for the filter dropdown typeahead. */
export async function getDistinctRecipients(
  params: { search?: string } = {},
  signal?: AbortSignal
) {
  return baseAPICall<EmailContact[]>(() =>
    clientInstance.get(`/email_integration/email_thread/distinct_recipients/`, {
      params,
      signal,
    })
  )
}

/** Assign one or more threads to a deal (bulk). */
export async function assignThreadsToDeal(data: {
  thread_ids: number[]
  deal_id: number
}) {
  return baseAPICall<{ assigned_count: number; deal_id: number }>(() =>
    clientInstance.post(`/email_integration/email_thread/assign_deal/`, data)
  )
}

/** Remove a thread from its deal. */
export async function removeThreadFromDeal(threadId: number) {
  return baseAPICall<{ thread_id: number; removed: boolean }>(() =>
    clientInstance.post(
      `/email_integration/email_thread/${threadId}/remove_deal/`
    )
  )
}

/** Hide a thread from the current user's inbox. */
export async function hideThread(threadId: number) {
  return baseAPICall<{ id: number; is_hidden: boolean }>(() =>
    clientInstance.post(`/email_integration/email_thread/${threadId}/hide/`)
  )
}

/** Unhide a previously hidden thread, restoring it to the inbox. */
export async function unhideThread(threadId: number) {
  return baseAPICall<{ id: number; is_hidden: boolean }>(() =>
    clientInstance.post(`/email_integration/email_thread/${threadId}/unhide/`)
  )
}

/** Search deals for autocomplete dropdown, scoped to a contact company. */
export async function searchDeals(
  params: { contact_company_id: number; q?: string; created_after?: string },
  signal?: AbortSignal
) {
  return baseAPICall<DealSearchResult[]>(() =>
    clientInstance.get(`/email_integration/email_thread/search_deals/`, {
      params,
      signal,
    })
  )
}
